import {RichEditor} from '@frontapp/editor-html';
import React, {FC, ReactNode, RefObject, useCallback} from 'react';
import {NEVER} from 'rxjs';

import {
  useObservableFromRef,
  useObservableRef,
} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {RepositionPopover} from '../../../../../libs/shared/ui/src/components/popovers/repositionPopover';
import {
  FormattingPopoverAnchor,
  FormattingPopoverAnchorTypesEnum,
  useFormattingPopoverAnchor,
} from '../../../common/editor/formattingPopover/formattingPopoverAnchor';
import {commentComposerScrollEditorClassName} from '../../composer/composerCommonClassnames';
import {CommentComposerFormattingLinkToolbar} from './commentComposerFormattingLinkToolbar';
import {CommentComposerFormattingToolbar} from './commentComposerFormattingToolbar';

/*
 * Props.
 */

interface CommentComposerFormattingPopoverProps {
  conversationId: number | undefined;
  editor: RichEditor;
  contentEditableRef: RefObject<HTMLElement>;
}

/*
 * Component.
 */

const scrollClassnames = [commentComposerScrollEditorClassName];
export const CommentComposerFormattingPopover: FC<CommentComposerFormattingPopoverProps> = (props) => {
  const {focus: focusService, selection: selectionService} = props.editor.services;
  const contentEditable$ = useObservableFromRef(props.contentEditableRef);
  const [contentWrapperRef, contentWrapper$] = useObservableRef<HTMLDivElement>();

  const [anchor, dismissAnchor] = useFormattingPopoverAnchor(
    selectionService,
    contentEditable$,
    contentWrapper$,
    NEVER,
    scrollClassnames,
  );

  const onRequestClose = useCallback(() => {
    dismissAnchor();
    focusService.focus();
  }, [dismissAnchor, focusService]);

  if (!anchor) {
    return null;
  }

  return (
    <RepositionPopover anchor={() => anchor.bounds} placement="top">
      <div ref={contentWrapperRef}>{renderContent(props, anchor, onRequestClose)}</div>
    </RepositionPopover>
  );
};

function renderContent(
  props: CommentComposerFormattingPopoverProps,
  anchor: FormattingPopoverAnchor,
  onRequestClose: () => void,
): ReactNode {
  switch (anchor.type) {
    case FormattingPopoverAnchorTypesEnum.LINK:
      return (
        <CommentComposerFormattingLinkToolbar
          conversationId={props.conversationId}
          linkElement={anchor.linkElement}
          onRequestClose={onRequestClose}
        />
      );

    case FormattingPopoverAnchorTypesEnum.SELECTION:
    default:
      return (
        <CommentComposerFormattingToolbar
          conversationId={props.conversationId}
          editor={props.editor}
          onRequestClose={onRequestClose}
        />
      );
  }
}
